import { useMemo, useState } from "react";
import { AxiosError } from "axios";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { IRodadaPedido } from "../interface/IRodadaPedido";
import { IQRCodeMesa, ISessaoMesa } from "../interface/ISessaoMesa";

/** Rotas do QR Code por mesa no ms-pedidos (`/pedidos/mesas`). */
export const useMesasQRCode = () => {
    const apiPedidos = useMemo(() => ApiFactory.getApi("MS_PEDIDOS"), []);
    const snackbar = useSnackbar();
    const [loading, setLoading] = useState(false);

    const tratarErro = (error: unknown, mensagemPadrao: string) => {
        const mensagem = error instanceof AxiosError ? error.response?.data?.message : undefined;
        snackbar(mensagem ?? mensagemPadrao, { severity: "error" });
    };

    const listarSessoes = async (): Promise<ISessaoMesa[]> => {
        try {
            const { data } = await apiPedidos.get("/mesas/sessoes");
            return data ?? [];
        } catch (error) {
            return [];
        }
    };

    const gerarQRCodes = async (marketplaceUuid: string): Promise<IQRCodeMesa[]> => {
        setLoading(true);
        try {
            const { data } = await apiPedidos.get(`/mesas/qrcodes/${marketplaceUuid}`);
            return data ?? [];
        } catch (error) {
            tratarErro(error, "Não foi possível gerar os QR Codes das mesas");
            return [];
        } finally {
            setLoading(false);
        }
    };

    const executarRodada = async (acao: "confirmar" | "recusar" | "entregar", pedidoUuid: string, rodadaUuid: string, motivo?: string): Promise<IRodadaPedido | null> => {
        if (loading) return null;
        setLoading(true);
        try {
            const { data } = await apiPedidos.post(`/mesas/${pedidoUuid}/rodadas/${rodadaUuid}/${acao}`, motivo ? { motivo } : undefined);
            return data;
        } catch (error) {
            tratarErro(error, "Não foi possível atualizar a rodada");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const reabrirSessao = async (sessaoUuid: string): Promise<ISessaoMesa | null> => {
        if (loading) return null;
        setLoading(true);
        try {
            const { data } = await apiPedidos.post(`/mesas/sessoes/${sessaoUuid}/reabrir`);
            snackbar("Mesa liberada para novos pedidos pelo QR Code", { severity: "success" });
            return data;
        } catch (error) {
            tratarErro(error, "Não foi possível reabrir a mesa");
            return null;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        listarSessoes,
        gerarQRCodes,
        confirmarRodada: (pedidoUuid: string, rodadaUuid: string) => executarRodada("confirmar", pedidoUuid, rodadaUuid),
        recusarRodada: (pedidoUuid: string, rodadaUuid: string, motivo?: string) => executarRodada("recusar", pedidoUuid, rodadaUuid, motivo),
        entregarRodada: (pedidoUuid: string, rodadaUuid: string) => executarRodada("entregar", pedidoUuid, rodadaUuid),
        reabrirSessao,
    };
};
